import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import CommonPopupsPopupMultiListComponent from 'webapp/components/common/popups/popup-multi-list';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterLineOrButtonComponent from 'webapp/components/filter/line-or-button';

import type { FilterIdType } from 'webapp/models-custom/filter-sort/base';
import type Filter from 'webapp/models-custom/filter-sort/filter';
import type PopupContext from 'webapp/models-custom/popup/context';

export default class FilterButtonComponent extends FilterLineOrButtonComponent {
	@service('popup') declare popupService: Service.Popup;

	get filterCount(): number {
		return this.args.model.filters.reduce((count, filter) => {
			// only count filters that have non-default selections
			if (!filter.isDefaultSelected) {
				// count all selected non-default items
				const nonDefaultItems = filter.selected.filter((id) => {
					return id !== 0 && id !== 'all';
				});
				return count + Math.max(nonDefaultItems.length, 1);
			}
			return count;
		}, 0);
	}

	get isFilterOn(): boolean {
		return this.filterCount !== 0;
	}

	buildItemListsForMultiList(): PopupListItem<string>[][] {
		const itemLists: PopupListItem<string>[][] = [];

		this.args.model.filters.forEach((filter) => {
			const list = this.parseFilterOptionsForPopup(filter as Filter<unknown, FilterIdType>);
			if (list.length) {
				itemLists.push(list);
			}
		});

		return itemLists;
	}

	updateMultiListPopupData(popup: PopupContext): void {
		if (popup.isOpen) {
			popup.data = {
				...popup.data,
				itemLists: this.buildItemListsForMultiList(),
			};
		}
	}

	@action
	openFilterDropdown(): void {
		if (!this.node) {
			return;
		}

		if (this.isPopupOpen) {
			return;
		}

		const attachTo = this.node.querySelector<HTMLElement>('ui-icon')!;

		if (!attachTo) {
			return;
		}

		if (this.popupService.isOpen(CommonPopupsPopupMultiListComponent)) {
			return;
		}

		const popup = this.popupService.createContext(
			CommonPopupsPopupMultiListComponent,
			attachTo,
			{
				popupClass: 'optionspopup filterLine filters multiple',
				theme: this.theme,
			},
		);

		popup.open({
			itemLists: this.buildItemListsForMultiList(),
			skipCloseOnAction: true,
			doAction: (actionName: string | undefined, id: unknown) => {
				if (actionName === 'selectView') {
					this.args.model.openView(id as string);
				} else if (actionName) {
					const filterId = +actionName.substr(-1),
						filter = this.args.model.findFilter(filterId);
					if (filter) {
						filter.selectOption(id as number | string);

						// update popup data to reflect new selection state
						this.updateMultiListPopupData(popup);
					}
				}
			},
		});

		this.isPopupOpen = true;
		popup.on(PopupEvent.CLOSED, this, () => {
			if (isAlive(this)) {
				this.isPopupOpen = false;
			}
		});
	}
	<template>
		<div
			id="filter-button"
			class="{{if this.showFilters 'has-filters'}}
				{{if this.isSearchActive 'search-active'}}
				{{if this.isFilterOn 'active'}}"
			role="button"
			{{on "click" this.openFilterDropdown}}
			{{didInsert this.setupView}}
		>
			<CommonUiIconComponent @name="Filter" @size="lg" />
			{{#if this.isFilterOn}}
				<span class="badge">{{this.filterCount}}</span>
			{{/if}}
		</div>
	</template>
}
